import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Dialog,
  Snackbar,
  Alert,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Vehicle, MileageReport } from '../../types/vehicle';
import { User } from '../../types';
import vehicleService from '../../services/vehicleService';
import { userService } from '../../services/userService';
import VehicleList from '../../components/vehicle/VehicleList';
import { VehicleFormStepper } from '../../components/vehicle/VehicleFormStepper';
import VehicleDetails from '../../components/vehicle/VehicleDetails';

const VehiclesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [initialTab, setInitialTab] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [reportUsers, setReportUsers] = useState<Record<string, { displayName: string }>>({});
  const [mileageReports, setMileageReports] = useState<MileageReport[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleAddVehicle = () => {
    setEditingVehicle(null);
    setIsFormOpen(true);
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setIsFormOpen(true);
  };

  const handleViewDetails = (vehicle: Vehicle, tab?: number) => {
    setSelectedVehicle(vehicle);
    if (typeof tab === 'number') {
      setInitialTab(tab);
    } else {
      setInitialTab(0); // Reset to default tab if not specified
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingVehicle(null);
  };

  const handleBackToList = () => {
    setSelectedVehicle(null);
    setInitialTab(0);
  };

  const handleSubmitVehicle = async (vehicleData: Partial<Vehicle>) => {
    try {
      if (!user?.organizationId) {
        throw new Error(t('vehicle.errors.noOrganization'));
      }

      if (editingVehicle) {
        // Update existing vehicle
        await vehicleService.updateVehicle(editingVehicle.id, vehicleData);
        // Fetch the updated vehicle data
        const updatedVehicle = await vehicleService.getVehicle(editingVehicle.id);
        if (updatedVehicle && selectedVehicle?.id === editingVehicle.id) {
          setSelectedVehicle(updatedVehicle);
        }
        setRefreshTrigger(prev => prev + 1);
        setSnackbar({
          open: true,
          message: t('vehicle.form.messages.updateSuccess'),
          severity: 'success',
        });
      } else {
        // Create new vehicle
        await vehicleService.createVehicle(
          user.organizationId,
          vehicleData as Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'createdBy'>,
          user.id
        );
        setRefreshTrigger(prev => prev + 1);
        setSnackbar({
          open: true,
          message: t('vehicle.form.messages.createSuccess'),
          severity: 'success',
        });
      }
      handleCloseForm();
    } catch (error) {
      console.error('Error submitting vehicle:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : t('common.error'),
        severity: 'error',
      });
    }
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    try {
      await vehicleService.deleteVehicle(vehicleId);
      setRefreshTrigger(prev => prev + 1);
      setSnackbar({
        open: true,
        message: t('vehicle.form.messages.deleteSuccess'),
        severity: 'success',
      });
      if (selectedVehicle?.id === vehicleId) {
        setSelectedVehicle(null);
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : t('common.error'),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const loadReportUsers = async () => {
    if (!user?.organizationId) return;
    try {
      const users = await userService.getOrganizationUsers(user.organizationId);
      const usersMap = users.reduce((acc: Record<string, { displayName: string }>, user: User) => {
        acc[user.id] = { displayName: user.displayName || user.email };
        return acc;
      }, {} as Record<string, { displayName: string }>);
      setReportUsers(usersMap);
    } catch (error) {
      console.error('Error loading users:', error);
      setSnackbar({
        open: true,
        message: t('vehicle.messages.loadUsersFailed'),
        severity: 'error'
      });
    }
  };

  const loadMileageReports = async (vehicleId: string) => {
    try {
      const reports = await vehicleService.getMileageReports(vehicleId);
      setMileageReports(reports);
    } catch (error) {
      console.error('Error loading mileage reports:', error);
      setSnackbar({
        open: true,
        message: t('vehicle.messages.loadMileageReportsFailed'),
        severity: 'error'
      });
    }
  };

  const handleVehicleUpdate = async () => {
    // Refresh the vehicle data if needed
    if (selectedVehicle) {
      const updatedVehicle = await vehicleService.getVehicle(selectedVehicle.id);
      if (updatedVehicle) {
        setSelectedVehicle(updatedVehicle);
        await loadMileageReports(updatedVehicle.id);
      }
    }
    // Reload users in case assignments changed
    await loadReportUsers();
  };

  useEffect(() => {
    loadReportUsers();
  }, [user?.organizationId]);

  useEffect(() => {
    if (selectedVehicle) {
      loadMileageReports(selectedVehicle.id);
    }
  }, [selectedVehicle?.id]);

  // Vérifier si le formulaire d'ajout de véhicule doit être ouvert automatiquement
  useEffect(() => {
    const state = location.state as { openAddVehicleForm?: boolean } | null;
    if (state?.openAddVehicleForm) {
      handleAddVehicle();
      // Réinitialiser l'état pour éviter de rouvrir le formulaire lors des navigations ultérieures
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {selectedVehicle ? (
          // Vehicle Details View
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToList}
              sx={{ mb: 2 }}
            >
              {t('vehicle.backToVehicles')}
            </Button>
              <VehicleDetails
                vehicle={selectedVehicle}
                mileageReports={mileageReports}
                reportUsers={reportUsers}
                onUpdate={handleVehicleUpdate}
                onEditVehicle={handleEditVehicle}
                onDeleteVehicle={handleDeleteVehicle}
              />
          </Box>
        ) : (
          // Vehicle List View
          <VehicleList
            onAddVehicle={handleAddVehicle}
            onEditVehicle={handleEditVehicle}
            onDeleteVehicle={handleDeleteVehicle}
            onViewDetails={handleViewDetails}
            onRefreshList={() => setRefreshTrigger(prev => prev + 1)}
          />
        )}

        {/* Vehicle Form Dialog */}
        <Dialog
          open={isFormOpen}
          onClose={handleCloseForm}
          maxWidth="md"
          fullWidth
          fullScreen={useMediaQuery(useTheme().breakpoints.down('sm'))}
        >
          <VehicleFormStepper
            open={isFormOpen}
            onClose={handleCloseForm}
            onSubmit={handleSubmitVehicle}
            vehicle={editingVehicle || undefined}
            title={editingVehicle ? t('vehicle.editVehicle') : t('vehicle.addVehicle')}
          />
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default VehiclesPage;
